import React, { useEffect, useState } from "react";
import { Alert, Collapse, Spin, Table, Tag } from "antd";
import GETJwtToken from "../../../redux/authentication/authentication";
import type {
  DiffFieldChange,
  DiffModifiedEntity,
  DiffTable,
  OrgDiff,
} from "../../../interfaces/ImportDiff.interface";
import { isDiffFile, postImportDiff, tableHasChanges } from "../utils/importDiff";

interface ImportDiffPanelProps {
  /** The organization the file is compared with: the one the wizard is editing. */
  orgId: string;
  /** The file chosen for import; the panel renders nothing until it is a .lnx/.bin. */
  file: File | null;
}

const changeColumns = [
  { title: "Field", dataIndex: "field", key: "field" },
  { title: "In this organization", dataIndex: "existing", key: "existing" },
  { title: "In the file", dataIndex: "imported", key: "imported" },
];

/**
 * Gives each key a React key that stays unique when a key repeats (a table
 * compared by content can list the same row twice): "<key>#<occurrence>".
 */
function withOccurrence<T>(items: T[], keyOf: (item: T) => string): { id: string; item: T }[] {
  const seen = new Map<string, number>();
  return items.map((item) => {
    const key = keyOf(item);
    const n = (seen.get(key) ?? 0) + 1;
    seen.set(key, n);
    return { id: `${key}#${n}`, item };
  });
}

function EntityList({ label, color, keys }: { label: string; color: string; keys: string[] }) {
  if (keys.length === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <Tag color={color}>{label}</Tag>
      <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
        {withOccurrence(keys, (key) => key).map(({ id, item }) => (
          <li key={id}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

function ModifiedList({ entities }: { entities: DiffModifiedEntity[] }) {
  if (entities.length === 0) return null;

  return (
    <div style={{ marginTop: 8 }}>
      <Tag color="orange">Modified</Tag>
      {withOccurrence(entities, (entity) => entity.key).map(({ id, item }) => (
        <div key={id} style={{ marginTop: 8 }}>
          <strong>{item.key}</strong>
          <Table<DiffFieldChange>
            columns={changeColumns}
            dataSource={item.changes}
            rowKey="field"
            pagination={false}
            size="small"
          />
        </div>
      ))}
    </div>
  );
}

function tableHeader(table: DiffTable) {
  return (
    <span>
      <strong>{table.table}</strong> <Tag>{table.same} same</Tag>
      {table.new.length > 0 && <Tag color="green">{table.new.length} new</Tag>}
      {table.modified.length > 0 && <Tag color="orange">{table.modified.length} modified</Tag>}
      {table.only_in_org.length > 0 && (
        <Tag color="blue">{table.only_in_org.length} only in organization</Tag>
      )}
    </span>
  );
}

/**
 * Compares the chosen .lnx with the organization being edited, through the
 * read-only POST /import/organization/diff, and shows per table what is the
 * same, new, modified or only in the organization. A failed comparison is
 * shown as a warning: it never blocks the import itself.
 */
function ImportDiffPanel({ orgId, file }: ImportDiffPanelProps) {
  const [diff, setDiff] = useState<OrgDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDiff(null);
    setError(null);
    if (!file || !isDiffFile(file.name)) return undefined;

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const token = await GETJwtToken();
        const result = await postImportDiff(
          process.env.REACT_APP_LIFT_NET_CLOUD_API_ONLINE,
          token,
          orgId,
          file,
        );
        if (!cancelled) setDiff(result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Comparison failed");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orgId, file]);

  if (!file || !isDiffFile(file.name)) return null;

  if (loading) {
    return (
      <div style={{ marginTop: 16 }}>
        <Spin tip="Comparing with this organization..." />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="warning"
        message="Could not compare with this organization"
        description={error}
        showIcon
        style={{ marginTop: 16 }}
      />
    );
  }

  if (!diff) return null;

  const changed = diff.tables.filter(tableHasChanges);

  return (
    <div style={{ marginTop: 16 }} data-testid="import-diff-panel">
      <strong>Compared with {diff.org_name}</strong>
      <div style={{ marginTop: 8 }}>
        <Tag>{diff.totals.same} same</Tag>
        <Tag color="green">{diff.totals.new} new</Tag>
        <Tag color="orange">{diff.totals.modified} modified</Tag>
        <Tag color="blue">{diff.totals.only_in_org} only in organization</Tag>
      </div>
      {diff.warnings.map((warning) => (
        <Alert key={warning} type="info" message={warning} showIcon style={{ marginTop: 8 }} />
      ))}
      {changed.length === 0 ? (
        <Alert
          type="success"
          message="Nothing in this file differs from the organization."
          showIcon
          style={{ marginTop: 8 }}
        />
      ) : (
        <Collapse style={{ marginTop: 8 }}>
          {changed.map((table) => (
            <Collapse.Panel header={tableHeader(table)} key={table.table}>
              <EntityList label="New" color="green" keys={table.new} />
              <ModifiedList entities={table.modified} />
              <EntityList label="Only in organization" color="blue" keys={table.only_in_org} />
            </Collapse.Panel>
          ))}
        </Collapse>
      )}
    </div>
  );
}

export default ImportDiffPanel;
